import {
  SpriteGenApi,
  SpriteGenParams,
  SpriteGenResult,
  AnimateSpriteParams,
  AnimateSpriteResult,
  CharacterAnimateParams,
  CharacterWorkflow,
  SpriteGenCosts,
  SpriteGenJobEvent,
} from './SpriteGenApi';
import { resolveGameSessionToken } from '../http/backendHttpDeps';
import type { BackendHttpDeps } from '../http/backendHttpDeps';

export class HttpSpriteGenApi implements SpriteGenApi {
  private readonly appId: string;
  private readonly deps: BackendHttpDeps;

  constructor(appId: string, deps: BackendHttpDeps) {
    this.appId = appId;
    this.deps = deps;
  }

  async generate(params: SpriteGenParams): Promise<SpriteGenResult> {
    return this.post<SpriteGenResult>('generate', params);
  }

  async animate(params: AnimateSpriteParams): Promise<AnimateSpriteResult> {
    return this.post<AnimateSpriteResult>('animate', params);
  }

  async characterAnimate(params: CharacterAnimateParams): Promise<{ jobId: string }> {
    return this.post<{ jobId: string }>('character-animate', params);
  }

  async listCharacterWorkflows(): Promise<CharacterWorkflow[]> {
    const data = await this.get<{ workflows: CharacterWorkflow[] }>(
      `/v1/spritegen/${this.appId}/character-workflows`,
      'List character workflows failed',
    );
    return data.workflows;
  }

  async listModels(): Promise<unknown> {
    const data = await this.get<{ models: unknown }>('/v1/spritegen/models', 'List sprite models failed');
    return data.models;
  }

  async getCosts(): Promise<SpriteGenCosts> {
    return this.get<SpriteGenCosts>('/v1/spritegen/costs', 'Get sprite costs failed');
  }

  async getCompletedJobs(): Promise<SpriteGenJobEvent[]> {
    const data = await this.get<{ jobs: SpriteGenJobEvent[] }>(
      `/v1/jobs/${this.appId}/spritegen/completed`,
      'Get completed sprite jobs failed',
    );
    return data.jobs;
  }

  private async get<T>(path: string, errorFallback: string): Promise<T> {
    const idToken = await this.deps.getIdToken();
    const baseUrl = this.deps.getCloudRunUrl();
    const headers: Record<string, string> = { 'Authorization': `Bearer ${idToken}` };

    const gst = await resolveGameSessionToken(this.deps.getGameSessionToken);
    if (gst) headers['x-h5-game-session'] = gst;

    const response = await fetch(`${baseUrl}${path}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(await this.extractError(response, errorFallback));
    }

    return response.json() as Promise<T>;
  }

  private async post<T>(endpoint: string, body: unknown): Promise<T> {
    const idToken = await this.deps.getIdToken();
    const baseUrl = this.deps.getCloudRunUrl();
    const url = `${baseUrl}/v1/spritegen/${this.appId}/${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    };

    const gst = await resolveGameSessionToken(this.deps.getGameSessionToken);
    if (gst) headers['x-h5-game-session'] = gst;

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(await this.extractError(response, `Sprite ${endpoint} failed`));
    }

    return response.json() as Promise<T>;
  }

  private async extractError(response: Response, fallback: string): Promise<string> {
    const errorText = await response.text();
    let errorMessage = `${fallback} (${response.status})`;
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.error || errorJson.message || errorMessage;
    } catch {
      if (errorText) errorMessage = errorText;
    }
    return errorMessage;
  }
}
